import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { getCurrency } from "helper/numberFormat";
import React, { useState, useEffect } from "react";
import { Fonts, FontColors } from "theme/fontConfig";

const MAX_DONATION = 999999.99;

// TODO: remove MOCK_LEDGER before production — used for UI testing only
const MOCK_LEDGER = {
  _id: "mock-ledger-id",
  checkout_title: "Add a Donation",
  ledger_name: "Additional Donation Options",
  preset_amounts: [5, 10, 20],
  allow_other: true,
  account_receivable: null,
};

/**
 * DonationBox — shown at checkout between billing info and CC form.
 * Only rendered when a ledger with show_on_checkout=true exists.
 *
 * Props:
 *  ledger          – ledger object { checkout_title, ledger_name, preset_amounts, allow_other, _id, account_receivable }
 *  loading         – true while fetching the ledger from API
 *  onDonationChange(amount, ledger) – called whenever selection changes; amount=0 means deselected
 */
const DonationBox = ({ ledger, loading, onDonationChange, resetKey = 0, onErrorChange }) => {
  const currency = getCurrency();
  const [selected, setSelected] = useState(null); // null | number | "other"
  const [otherAmount, setOtherAmount] = useState("");
  const [otherError, setOtherError] = useState("");

  useEffect(() => {
    setSelected(null);
    setOtherAmount("");
    setOtherError("");
    if (onErrorChange) onErrorChange(false);
  }, [resetKey]);

  const activeLedger = ledger || MOCK_LEDGER;

  if (!loading && !ledger) return null;

  const presetAmounts = activeLedger.preset_amounts || [];
  const allowOther = !!activeLedger.allow_other;

  const handleSelect = (amount) => {
    setOtherAmount("");
    setOtherError("");
    if (onErrorChange) onErrorChange(false);
    if (selected === amount) {
      setSelected(null);
      onDonationChange(0, null);
    } else {
      setSelected(amount);
      onDonationChange(amount, activeLedger);
    }
  };

  const handleOtherClick = () => {
    if (selected === "other") {
      setSelected(null);
      setOtherAmount("");
      setOtherError("");
      if (onErrorChange) onErrorChange(false);
      onDonationChange(0, null);
    } else {
      setSelected("other");
      onDonationChange(0, null);
    }
  };

  const handleOtherAmountChange = (e) => {
    const val = e.target.value;
    setOtherAmount(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > MAX_DONATION) {
      setOtherError(`Maximum donation amount is ${currency}${MAX_DONATION.toLocaleString()}`);
      onDonationChange(0, null);
      if (onErrorChange) onErrorChange(true);
    } else if (!isNaN(num) && num > 0) {
      setOtherError("");
      onDonationChange(num, activeLedger);
      if (onErrorChange) onErrorChange(false);
    } else {
      setOtherError("");
      onDonationChange(0, null);
      if (onErrorChange) onErrorChange(false);
    }
  };

  const btnStyle = (isActive) => ({
    height: 56,
    minWidth: 90,
    padding: "0 20px",
    borderRadius: 12,
    border: isActive ? "2px solid #2555EF" : "2px solid #E2E8F0",
    background: isActive ? "rgba(37,85,239,0.06)" : "#fff",
    color: isActive ? "#2555EF" : "#0F172A",
    fontWeight: Fonts.GeneralSubHeaderFont.fontWeight,
    fontSize: Fonts.GeneralSubHeaderFont.fontSize,
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s, color 0.15s",
  });

  return (
    <Paper
      sx={{
        marginTop: "33px",
        borderRadius: "10px",
        padding: "24px 28px",
        backgroundColor: "#fff",
      }}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <CircularProgress size={28} sx={{ color: FontColors.Blue002E5C }} />
        </div>
      ) : (
        <>
          <h2
            style={{
              fontWeight: Fonts.GeneralHeaderFont.fontWeight,
              fontSize: Fonts.GeneralHeaderFont.fontSize,
              color: FontColors.Blue002E5C,
              lineHeight: "32px",
              margin: "0 0 6px 0",
            }}
          >
            {activeLedger.checkout_title || "Add a Donation"}
          </h2>
          <p
            style={{
              fontSize: Fonts.GeneralBodyFont.fontSize,
              color: FontColors.Slate64748B,
              margin: "0 0 20px 0",
            }}
          >
            {activeLedger.ledger_name}
          </p>

          {/* Amount buttons */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                type="button"
                onClick={() => handleSelect(amount)}
                style={btnStyle(selected === amount)}
              >
                {currency}
                {amount}
              </button>
            ))}
            {allowOther && (
              <button
                type="button"
                onClick={handleOtherClick}
                style={btnStyle(selected === "other")}
              >
                Other
              </button>
            )}
          </div>

          {/* Custom amount input — shown only when Other is selected */}
          {selected === "other" && (
            <div style={{ marginTop: 20 }}>
              <p
                style={{
                  fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight,
                  fontSize: Fonts.GeneralSmallHeaderFont.fontSize,
                  color: FontColors.Slate0F172A,
                  margin: "0 0 8px 0",
                }}
              >
                Custom Amount
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: `1.5px solid ${otherError ? "#EF4444" : "#E2E8F0"}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  background: "#F8FAFC",
                }}
              >
                <span
                  style={{
                    fontSize: Fonts.GeneralLargeBodyFont.fontSize,
                    color: FontColors.Slate94A3B8,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                >
                  {currency}
                </span>
                <input
                  type="number"
                  min="0"
                  max={MAX_DONATION}
                  step="0.01"
                  value={otherAmount}
                  onWheel={(e) => e.target.blur()}
                  onChange={handleOtherAmountChange}
                  placeholder="0.00"
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: Fonts.GeneralLargeBodyFont.fontSize,
                    color: FontColors.Slate0F172A,
                    outline: "none",
                    width: "100%",
                  }}
                />
              </div>
              {otherError && (
                <p style={{ color: FontColors.RedEF4444, fontSize: Fonts.GeneralCaptionFont.fontSize, margin: "6px 0 0 0" }}>
                  {otherError}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Paper>
  );
};

export default DonationBox;
